import { ApiServiceCollection } from "../ApiServiceCollection.class";
import { ApiServiceEndpoint } from "../api.types";
import { ApiResponse } from "../RequestManager.class";
import { BundleAttribution } from "../bundle/types";
import { RowStatus } from "../../components/TableWithButtons/TableWithButtons";
import { Attribution, OrganizationAttribution } from "../attribution/types";
import { QuoteSchema } from "./QuoteExperienceProcessor";
import { BundleService } from "../bundle/BundleService.class";
import { TableQuery } from "../mocks/database/MockDatabase";
import { AttributionService } from "../attribution/AttributionService.class";
import { OrganizationService } from "../organization/OrganizationService.class";

export type QuoteExperienceData = {
    displayName: string;
    bundleAttrs: BundleAttribution[];
    partnerAttributionGuid?: string;
    desktopImage?: Blob;
    mobileImage?: Blob;
    faviconBaseImage?: Blob;
    faviconPng?: Blob;
    faviconPngDataUri?: string;
    faviconIco?: Blob;
    faviconIcoDataUri?: string;
    formDataByType: Record<string, any>;
    attrStatusByType: Record<string, RowStatus>;
};

export type SaveQuoteExperienceParams = {
    bundleGuid?: string;
    organizationGuid?: string;
    displayName: string;
    desktopImage?: File | Blob;
    desktopImageChanged: boolean;
    mobileImage?: File | Blob;
    mobileImageChanged: boolean;
    faviconBaseImage?: File | Blob;
    faviconBaseImageChanged: boolean;
    faviconPng?: File | Blob;
    faviconIco?: File | Blob;
    faviconProcessed: boolean;
    formDataByType: Record<string, any>;
    bundleAttrs: BundleAttribution[];
    existingPartnerAttributionGuid?: string;
    selectedPartnerAttributionGuid?: string;
    orgAttrs: OrganizationAttribution[];
    quoteSchemas: QuoteSchema[];
    attrStatusByType: Record<string, RowStatus>;
};

export class QuoteExperienceService extends ApiServiceCollection {
    protected transformMockQuery: (tableName: string, query: TableQuery) => TableQuery = (_, query) => query.map(entry => {
        if (entry.fieldName === 'bundle_guid') {
            entry.fieldName = 'bundle_id';
            entry.value = this.mockCRUD!.resolveGuidToId('bundles', entry.value);
        }
        return entry;
    });

    uploadLogo: ApiServiceEndpoint = (
        { bundleGuid, filepath, logo }:
        {
            bundleGuid: string,
            filepath: string,
            logo: File
        }
    ) => {
        return this.uploadFile({ bundleGuid, filepath, logo });
    }

    downloadLogo: ApiServiceEndpoint = (
        { bundleGuid, filepath }:
        {
            bundleGuid: string,
            filepath: string
        }
    ) => {
        return this.downloadFile({ bundleGuid, filepath });
    }

    uploadFile: ApiServiceEndpoint = (
        { bundleGuid, filepath, file }:
        {
            bundleGuid: string,
            filepath: string,
            file: File
        }
    ) => {
        const formData = new FormData();
        formData.append('file', file);

        return this.standardAuthRequest(`${process.env.REACT_APP_MANAGEMENT_API_URL}/quote-experience/${bundleGuid}/file/${filepath}`, {
            method: 'POST',
            body: formData
        }, {
            mock: this.mocked,
            getMockStatus: () => this.getNextMockStatus('QuoteExperienceService.uploadLogo')
        })
        .then(resp => {
            if (this.mocked) {
                const bundle_id = this.mockCRUD!.resolveGuidToId('bundles', bundleGuid);
                this.mockCRUD!.db().getTableByName('cdn').createRecord({
                    bundle_id,
                    filepath: filepath,
                    file: file
                });
            }
            return {
                response: resp.response!,
                success: true,
                jsonBody: null
            }
        });
    }

    downloadFile: ApiServiceEndpoint<Blob> = (
        { bundleGuid, filepath }:
        {
            bundleGuid: string,
            filepath: string
        }
    ) => {
        return this.standardAuthRequest(`${process.env.REACT_APP_MANAGEMENT_API_URL}/quote-experience/${bundleGuid}/file/${filepath}`, {
            method: 'GET'
        }, {
            mock: this.mocked,
            mockData: this.getMockDataSingular({
                tableName: 'cdn', 
                queryParams: {
                    bundle_guid: bundleGuid,
                    filepath
                }
            }),
            getMockStatus: () => this.getNextMockStatus('QuoteExperienceService.downloadLogo'),
            transformResponsePayload: async (apiResponse: ApiResponse) => ({
                response: apiResponse.response,
                success: true,
                blobBody: await apiResponse.response?.blob()
            })
        })
        .catch(error => error);
    }

    listAssetFiles: ApiServiceEndpoint = (
        { bundleGuid, folderPath }:
        {
            bundleGuid: string,
            folderPath: string
        }
    ) => {
        return this.standardAuthRequest(
            `${process.env.REACT_APP_MANAGEMENT_API_URL}/quote-experience/${bundleGuid}/${folderPath}`,
            {
                method: 'GET',
                headers: { ...this.getJSONHeaders() }
            },
            {
                mock: this.mocked,
                mockData: this.getMockData({
                    tableName: 'cdn',
                    queryParams: [
                        {
                            fieldName: 'bundle_guid',
                            value: bundleGuid
                        },
                        {
                            fieldName: 'filepath',
                            operator: 'startswith',
                            value: folderPath
                        }
                    ]
                }),
                getMockStatus: () => this.getNextMockStatus('QuoteExperienceService.listAssetFiles'),
                transformResponsePayload: async (apiResponse: ApiResponse) => ({
                    response: apiResponse.response,
                    success: true,
                    jsonBody: await apiResponse.response?.json()
                })
            }
        )
        .catch(error => error);
    }

    publish: ApiServiceEndpoint = (
        { bundleGuid }:
        {
            bundleGuid: string
        }
    ) => {
        return this.standardAuthRequest(`${process.env.REACT_APP_MANAGEMENT_API_URL}/quote-experience/${bundleGuid}/publish`, {
            method: 'POST'
        }, {
            mock: this.mocked,
            mockData: {
                data: null,
                status: 204
            },
            getMockStatus: () => this.getNextMockStatus('QuoteExperienceService.publish')
        })
        .then(resp => {
            console.log("Pubish QE - Success")
            return {
                success: true,
                response: resp.response!,
            }
        })
        .catch(error => {
            console.log("Pubish QE - Error encountered", error);
            return error;
        });
    }

    uploadAssetImage: ApiServiceEndpoint = (
        { bundleGuid, imageName, image }:
        {
            bundleGuid: string,
            imageName: string,
            image: File
        }
    ) => {
        const formData = new FormData();
        formData.append('file', image);

        return this.standardAuthRequest(
            `${process.env.REACT_APP_MANAGEMENT_API_URL}/quote-experience/${bundleGuid}/file/assets/images/${imageName}`,
            {
                method: 'POST',
                body: formData
            }, {
                mock: this.mocked,
                mockData: {
                    data: null,
                    status: 204
                },
                getMockStatus: () => this.getNextMockStatus('QuoteExperienceService.uploadAssetImage')
            }
        )
        .then(resp => {
            if (this.mocked) {
                const bundle_id = this.mockCRUD!.resolveGuidToId('bundles', bundleGuid);
                this.mockCRUD!.db().getTableByName('cdn').createRecord({
                    bundle_id,
                    filepath: imageName,
                    file: image
                });
            }
            return {
                response: resp.response!,
                success: true
            }
        })
        .catch(error => error);
    }

    loadQuoteExperienceData = async (
        bundleGuid: string,
        services: {
            BundleService: BundleService;
            AttributionService: AttributionService;
        }
    ): Promise<QuoteExperienceData> => {
        const { BundleService, AttributionService } = services;

        // Load bundle
        const getBundle = BundleService.getBundleById;
        let bResp = await getBundle(bundleGuid);
        while (bResp.retry) {
            bResp = await bResp.retry.catch(error => error);
        }
        const displayName = bResp.jsonBody!.displayName;

        // Load bundle attributions
        const getBundleAttributions = BundleService.getAttributions;
        let bundleAttrResp = await getBundleAttributions(bundleGuid);
        while (bundleAttrResp.retry) {
            bundleAttrResp = await bundleAttrResp.retry.catch(error => error);
        }
        let allBundleAttrs: BundleAttribution[] = [];
        if (!bundleAttrResp.error) {
            // TODO - Should error out
            allBundleAttrs = bundleAttrResp.jsonBody!;
        }
        const bundleAttrs = allBundleAttrs.filter((ba: BundleAttribution) => ba.type !== 'PARTNER');
        const bundlePartnerAttr = allBundleAttrs.find((ba: BundleAttribution) => ba.type === 'PARTNER');

        const logosToLoad: string[] = [
            'assets/logos/desktop-logo.svg',
            'assets/logos/mobile-logo.svg',
            'assets/favicon/favicon.svg',
            'assets/favicon/favicon-96x96.png',
            'assets/favicon/favicon.ico',
        ];

        // this.loadImage(bundleGuid, 'assets/logos/desktop-logo.svg'),

        // Load images
        const [desktopLogoResp, mobileLogoResp, faviconBaseResp, faviconPngResp, faviconIcoResp] = await Promise.all(
            logosToLoad.map(logo => {
                return new Promise(async (resolve, reject) => {
                    let resp = await this.downloadLogo(bundleGuid, logo);
                    while (resp.retry) {
                        resp = await resp.retry.catch(error => error);
                    }
                    if (!resp.error) {
                        resolve(resp.blobBody!);
                    } else {
                        reject(undefined);
                    }
                })
            })
        ) as (Blob | undefined)[];

        // Load attribution data
        const getAttributionByGuid = AttributionService.getAttributionById;
        const entries = await Promise.all(
            bundleAttrs.map(attr => {
                return new Promise<[string, Attribution | null]>(async (resolve, reject) => {
                    let aResp = await getAttributionByGuid(attr.attributionGuid);
                    while (aResp.retry) {
                        aResp = await aResp.retry.catch(error => error);
                    }
                    let jsonBody: Attribution | null = null;
                    if (!aResp.error) {
                        jsonBody = aResp.jsonBody!;
                    }

                    resolve([attr.attributionGuid, jsonBody as Attribution]);
                })
            })
        );

        const byGuid: Record<string, Attribution | null> = Object.fromEntries(entries);
        const formDataByType: Record<string, any> = {};
        const attrStatusByType: Record<string, RowStatus> = {};

        for (const [, a] of Object.entries(byGuid)) {
            if (!a?.type) {
                continue;
            }
            const schemaType = this.normalizeType(a?.type);
            const current = formDataByType[schemaType] ?? {};
            const merged = { ...current, ...(a?.properties ?? {}) };
            formDataByType[schemaType] = merged;
            attrStatusByType[schemaType] = a.status;
        }

        return {
            displayName,
            bundleAttrs,
            partnerAttributionGuid: bundlePartnerAttr?.attributionGuid,
            desktopImage: desktopLogoResp,
            mobileImage: mobileLogoResp,
            faviconBaseImage: faviconBaseResp,
            faviconPng: faviconPngResp,
            faviconPngDataUri: faviconPngResp ? URL.createObjectURL(faviconPngResp) : undefined,
            faviconIco: faviconIcoResp,
            faviconIcoDataUri: faviconIcoResp ? URL.createObjectURL(faviconIcoResp) : undefined,
            formDataByType,
            attrStatusByType,
        };
    };

    loadImage = async (bundleGuid: string, filepath: string): Promise<Blob | undefined> => {
        const resp = await this.downloadLogo({ bundleGuid, filepath });
        return resp.blobBody || undefined;
    };

    normalizeType = (t: string) => String(t || '').toUpperCase();

    saveQuoteExperience = async (
        params: SaveQuoteExperienceParams,
        services: {
            BundleService: BundleService;
            AttributionService: AttributionService;
            OrganizationService: OrganizationService;
        },
        onError: (error: string, schemaType?: string) => void,
        onBundleCreated?: (bundleGuid: string) => void
    ): Promise<string | null> => {
        const { BundleService, AttributionService, OrganizationService } = services;
        let bundleGuid: string | null = params.bundleGuid || null;

        // Create bundle if needed
        if (!bundleGuid) {
            bundleGuid = await this.createBundle(
                BundleService,
                params.displayName,
                params.organizationGuid!,
                onError
            );
            if (!bundleGuid) {
                // TODO - throw an error with a message about what occurred so the display can simply show the error message
                // instead of checking "if it's null then I know the bundle failed to create"
                return null
            };
            onBundleCreated?.(bundleGuid);
        }

        // At this point, bundleGuid is guaranteed to be a string
        const bundleGuidStr = bundleGuid as string;

        // Upload images
        const imageUploadSuccess = await this.uploadImages(bundleGuidStr, params, onError);
        if (!imageUploadSuccess) {
            // TODO - again here, throw an error with a message about what occurred so the display can simply show the error message
            // instead of checking "if it's null then I know the images failed to upload"
            return null
        };

        // Handle partner attribution
        await this.handlePartnerAttribution(
            BundleService,
            bundleGuidStr,
            params.existingPartnerAttributionGuid,
            params.selectedPartnerAttributionGuid,
            params.orgAttrs
        );

        // Save form data
        const formSaveSuccess = await this.saveFormData(
            bundleGuidStr,
            params.formDataByType,
            params.attrStatusByType,
            params.bundleAttrs,
            params.quoteSchemas,
            params.displayName,
            params.organizationGuid!,
            { BundleService, AttributionService, OrganizationService },
            onError
        );
        if (!formSaveSuccess) return null;

        // Publish
        await this.publish({ bundleGuid: bundleGuidStr });

        return bundleGuidStr;
    };

    private createBundle = async (
        BundleService: BundleService,
        displayName: string,
        organizationGuid: string,
        onError: (error: string) => void
    ): Promise<string | null> => {
        try {
            const postBundle = BundleService.createBundle;
            const bResp = await postBundle({
                displayName,
                organizationGuid,
                type: "QUOTE_EXPERIENCE"
            });
            const bundleGuid = bResp?.textBody;
            if (!bundleGuid) {
                throw new Error('Unable to create Quote Experience.');
            }
            return bundleGuid;
        } catch (err: any) {
            console.error('Failed to save Quote Experience', err);
            onError(
                err?.status === 409
                    ? 'A Quote Experience with this name already exists. Please choose a different name.'
                    : 'Failed to save Quote Experience'
            );
            return null;
        }
    };

    private uploadImages = async (
        bundleGuid: string,
        params: SaveQuoteExperienceParams,
        onError: (error: string) => void
    ): Promise<boolean> => {
        try {
            if (params.desktopImageChanged && params.desktopImage) {
                await this.uploadLogo({
                    bundleGuid,
                    filepath: 'assets/logos/desktop-logo.svg',
                    logo: params.desktopImage as File
                });
            }

            if (params.mobileImageChanged && params.mobileImage) {
                await this.uploadLogo({
                    bundleGuid,
                    filepath: 'assets/logos/mobile-logo.svg',
                    logo: params.mobileImage as File
                });
            }

            if (params.faviconBaseImageChanged && params.faviconBaseImage && params.faviconProcessed) {
                await this.uploadLogo({
                    bundleGuid,
                    filepath: 'assets/favicon/favicon.svg',
                    logo: params.faviconBaseImage as File
                });
                await this.uploadLogo({
                    bundleGuid,
                    filepath: 'assets/favicon/favicon-96x96.png',
                    logo: params.faviconPng as File
                });
                await this.uploadLogo({
                    bundleGuid,
                    filepath: 'assets/favicon/favicon.ico',
                    logo: params.faviconIco as File
                });
            }

            return true;
        } catch (err: any) {
            console.error('Failed to upload images', err);
            onError(`Failed to upload images: ${err?.message || 'Unknown error'}`);
            return false;
        }
    };


    handlePartnerAttribution = async (
        BundleService: BundleService,
        bundleGuid: string,
        existingPartnerAttributionGuid?: string,
        selectedPartnerAttributionGuid?: string,
        orgAttrs: OrganizationAttribution[] = []
    ): Promise<void> => {
        if (existingPartnerAttributionGuid === selectedPartnerAttributionGuid) {
            return;
        }
        
        let bundleAttrs: BundleAttribution[] = [];
        if (bundleGuid) {
            let bundleAttrsResp = await BundleService.getAttributions(bundleGuid);
            while (bundleAttrsResp.retry) {
                bundleAttrsResp = await bundleAttrsResp.retry.catch(error => error)
            }
            if (bundleAttrsResp.error) {
                throw new Error('Failed to fetch existing attributions for this Quote Experience');
            }
            bundleAttrs = bundleAttrsResp.jsonBody!;
        }

        // Remove existing
        if (existingPartnerAttributionGuid) {
            const bundleAttribution = bundleAttrs.find(ba => ba.attributionGuid === existingPartnerAttributionGuid);
            if (bundleAttribution) {
                await BundleService.deleteBundleAttribution({
                    bundleGuid,
                    organizationAttributionGuid: bundleAttribution.organizationAttributionGuid
                });
            }
        }

        // Add new
        if (selectedPartnerAttributionGuid) {
            const orgAttr = orgAttrs.find(oa => oa.attributionGuid === selectedPartnerAttributionGuid);
            if (orgAttr) {
                await BundleService.addBundleAttribution({
                    bundleGuid,
                    organizationAttributionGuid: orgAttr.organizationAttributionGuid
                });
            }
        }
    };

    private saveFormData = async (
        bundleGuid: string,
        formDataByType: Record<string, any>,
        attrStatusByType: Record<string, RowStatus> = {},
        bundleAttrs: BundleAttribution[],
        quoteSchemas: QuoteSchema[],
        displayName: string,
        organizationGuid: string,
        services: {
            BundleService: BundleService;
            AttributionService: AttributionService;
            OrganizationService: OrganizationService;
        },
        onError: (error: string, schemaType?: string) => void
    ): Promise<boolean> => {
        const { BundleService, AttributionService, OrganizationService } = services;
        const schemaTypes = Array.from(new Set(Object.keys(formDataByType).concat(Object.keys(attrStatusByType))));

        for (const st of schemaTypes) {
            try {
                const formData = formDataByType[st] ?? {};
                const existingAttr = bundleAttrs.find(ba => this.normalizeType(ba.type) === this.normalizeType(st));
                const currentSchema = quoteSchemas.find(schema => this.normalizeType(schema.schema_type) === this.normalizeType(st));
                if (!currentSchema) {
                    // Skip saving this attribution
                    continue;
                }

                if (existingAttr) {
                    await this.patchAttribution({
                            attributionGuid: existingAttr.attributionGuid,
                            status: attrStatusByType[st] || 'ACTIVE',
                            properties: formData,
                            typeVersion: currentSchema.schema_type_version
                        },
                        { AttributionService }
                    );
                } else {
                    await this.createAttribution(
                        bundleGuid,
                        st,
                        formData,
                        displayName,
                        organizationGuid,
                        attrStatusByType[st] || 'ACTIVE',
                        quoteSchemas,
                        { BundleService, AttributionService, OrganizationService }
                    );
                }
            } catch (err: any) {
                console.error(`Failed to save ${st}`, err);
                onError(err?.message || 'Failed to save this form.', st);

                // Reload bundle attributions after error - TODO - or maybe have the caler handle it on error instead of trying to edit the params it provided us by reference???
                const getBundleAttributions = BundleService.getAttributions;
                const bundleAttrResp = await getBundleAttributions(bundleGuid);
                // TODO - Change this to not modify in place and to return an object with a status and specific bits of data for updating
                bundleAttrs.length = 0;
                bundleAttrs.push(
                    ...(bundleAttrResp?.jsonBody || []).filter((ba: BundleAttribution) => ba.type !== 'PARTNER')
                );

                return false;
            }
        }

        return true;
    };

    private createAttribution = async (
        bundleGuid: string,
        schemaType: string,
        properties: Record<string, any>,
        displayName: string,
        organizationGuid: string,
        status: RowStatus,
        quoteSchemas: QuoteSchema[],
        services: {
            BundleService: BundleService;
            AttributionService: AttributionService;
            OrganizationService: OrganizationService;
        }
    ): Promise<void> => {
        const { BundleService, AttributionService, OrganizationService } = services;
        const postAttribution = AttributionService.createAttribution;
        const postOrgAttr = OrganizationService.createOrganizationAttribution;
        const postBundleAttr = BundleService.addBundleAttribution;

        const currentSchema = quoteSchemas.find(schema => schema.schema_type === schemaType);
        if (!currentSchema) return;

        const aResp = await postAttribution({
            displayName,
            status,
            type: schemaType,
            properties,
            typeVersion: currentSchema.schema_type_version
        });
        const attributionGuid = aResp?.textBody;
        if (!attributionGuid) return;

        const oaResp = await postOrgAttr({
            organizationGuid,
            attributionGuid,
            required: false
        });
        const organizationAttributionGuid = oaResp?.textBody;
        if (!organizationAttributionGuid) return;

        await postBundleAttr({
            bundleGuid,
            organizationAttributionGuid
        });
    };

    private patchAttribution = async (
        params: {
            attributionGuid: string,
            status: RowStatus,
            properties: Record<string, any>,
            typeVersion?: number;
        },
        services: {
            AttributionService: AttributionService
        }
    ): Promise<void> => {
        const patchAttribution = services.AttributionService.updateAttribution;
        await patchAttribution({
            attributionGuid: params.attributionGuid,
            status: params.status,
            properties: params.properties,
            typeVersion: params.typeVersion
        });
    };
}